import type { InvoiceAttachment } from '@coreledger/shared-types';
import { API_BASE as BASE } from './client';

const tok = () => sessionStorage.getItem('cl_token') ?? '';
const auth = () => ({ Authorization: `Bearer ${tok()}` });

async function checkOk(res: Response, msg: string) {
  if (!res.ok) {
    const body = await res.json().catch(() => ({})) as { message?: string };
    throw new Error(body.message ?? `${msg} (${res.status})`);
  }
}

export const attachmentsApi = {
  list: async (invoiceId: string): Promise<InvoiceAttachment[]> => {
    const res = await fetch(`${BASE}/invoices/${invoiceId}/attachments`, { headers: auth() });
    await checkOk(res, 'Failed to load attachments');
    return res.json();
  },

  upload: async (invoiceId: string, file: File): Promise<InvoiceAttachment> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/invoices/${invoiceId}/attachments`, {
      method: 'POST',
      headers: auth(),
      body: fd,
    });
    await checkOk(res, 'Upload failed');
    return res.json();
  },

  download: async (id: string, filename: string): Promise<void> => {
    const res = await fetch(`${BASE}/attachments/${id}/download`, { headers: auth() });
    await checkOk(res, 'Download failed');
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },

  remove: async (id: string): Promise<void> => {
    const res = await fetch(`${BASE}/attachments/${id}`, { method: 'DELETE', headers: auth() });
    await checkOk(res, 'Delete failed');
  },
};

/**
 * Enqueue a full-export job, poll until complete, then trigger download.
 * Offloads ZIP generation off the request thread (BullMQ worker handles it).
 */
export async function downloadBackup(onProgress?: (msg: string) => void): Promise<void> {
  // 1. Enqueue the job
  const startRes = await fetch(`${BASE}/jobs/export`, { method: 'POST', headers: auth() });
  if (!startRes.ok) throw new Error(`Export request failed (${startRes.status})`);
  const { jobId } = await startRes.json() as { jobId: string };

  onProgress?.('Export queued, preparing download…');

  // 2. Poll until completed or failed (max 60s)
  const deadline = Date.now() + 60_000;
  while (Date.now() < deadline) {
    await new Promise(r => setTimeout(r, 1500));
    const statusRes = await fetch(`${BASE}/jobs/${jobId}/status`, { headers: auth() });
    if (!statusRes.ok) throw new Error('Could not check export status');
    const { status, failedReason } = await statusRes.json() as { status: string; failedReason?: string };
    if (status === 'failed') throw new Error(failedReason ?? 'Export failed');
    if (status === 'completed') break;
    onProgress?.('Generating export…');
  }

  // 3. Download the result
  const downloadRes = await fetch(`${BASE}/jobs/${jobId}/download`, { headers: auth() });
  if (!downloadRes.ok) throw new Error(`Download failed (${downloadRes.status})`);
  const blob = await downloadRes.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `coreledger-backup-${new Date().toISOString().slice(0, 10)}.zip`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
